import React, { useState } from "react";
import { IDButton } from "@/marketing/brand";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackEvent } from "@/lib/track";
import { pixelTrack } from "@/lib/metaPixel";

/*
 * Shared newsletter signup form (PUBLIC). Posts to /api/newsletter/subscribe
 * (MongoDB + MailerLite when configured) and fires the newsletter_signup +
 * Meta "Lead" conversion events.
 *
 * Variants:
 *   - "stacked" (default): name + email + full-width button (Sunday Reset page).
 *   - "hero": one-tap inline email + button in a single row (stacks on mobile),
 *     used in the homepage hero to remove the click-through step.
 *
 * Props: dark, testidPrefix, cta, source (overrides URL-derived source),
 *        showName, variant, microcopy.
 */
export function SignupForm({
  dark = false,
  testidPrefix = "signup",
  cta = "Send me the Sunday Reset",
  source,
  showName = true,
  variant = "stacked",
  microcopy = "A free weekly letter. Grace over shame. Unsubscribe anytime.",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email.");
    setBusy(true);
    try {
      // Tag where the sign-up came from. An explicit `source` wins; otherwise the
      // Life Audit funnel (?from=life-audit) is honored, defaulting to sunday-reset.
      const from = new URLSearchParams(window.location.search).get("from");
      const src = source || (from === "life-audit" ? "life-audit" : "sunday-reset");
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim(), name: name.trim(), source: src });
      trackEvent("newsletter_signup");
      pixelTrack("Lead", { content_name: "Newsletter — Sunday Reset", content_category: src }, { email: email.trim() });
      setDone(data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
    } finally { setBusy(false); }
  };

  const inputCls = `id-sans w-full rounded-full px-5 py-4 text-[15px] outline-none transition-colors ${
    dark
      ? "bg-white/95 text-[var(--id-charcoal)] placeholder:text-[var(--id-charcoal)]/45 focus:ring-2 focus:ring-[var(--id-gold)]"
      : "border border-[color:rgba(32,32,32,0.12)] bg-white text-[var(--id-charcoal)] placeholder:text-[var(--id-charcoal)]/40 focus:border-[var(--id-gold)]"
  }`;

  if (done) {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className={`rounded-2xl border px-6 py-6 text-center ${dark ? "border-white/15 bg-white/5" : "border-[color:rgba(32,32,32,0.10)] bg-[var(--id-ivory)]"}`}>
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--id-gold)]/15 text-[var(--id-gold)]">
          <Check size={22} strokeWidth={2} />
        </div>
        <p className={`id-serif mt-4 text-xl ${dark ? "text-[var(--id-ivory)]" : "text-[var(--id-charcoal)]"}`}>{done}</p>
        <p className={`id-sans mt-2 text-sm ${dark ? "text-[var(--id-ivory)]/60" : "text-[var(--id-charcoal)]/55"}`}>
          Your welcome gift will be included with your first letter.
        </p>
      </div>
    );
  }

  // Compact inline hero variant — email + button in one row (stacks on mobile).
  if (variant === "hero") {
    return (
      <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="w-full max-w-[520px]">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            data-testid={`${testidPrefix}-email`}
            aria-label="Email address"
            className={`${inputCls} sm:flex-1`}
          />
          <IDButton
            as="button"
            type="submit"
            variant={dark ? "gold" : "solid"}
            disabled={busy}
            testid={`${testidPrefix}-submit`}
            className="justify-center whitespace-nowrap !py-4 text-[14px] sm:!px-8">
            {busy ? "Sending…" : cta}
          </IDButton>
        </div>
        {showName && (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name (optional)"
            data-testid={`${testidPrefix}-name`}
            aria-label="First name"
            className={`${inputCls} mt-3`}
          />
        )}
        <p className={`id-sans mt-3.5 text-[12px] ${dark ? "text-[var(--id-ivory)]/55" : "text-[var(--id-charcoal)]/50"}`}>
          {microcopy}
        </p>
      </form>
    );
  }

  // Default stacked variant (name + email grid, full-width button).
  return (
    <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="w-full">
      <div className={`grid gap-3 ${showName ? "sm:grid-cols-2" : ""}`}>
        {showName && (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name (optional)"
            data-testid={`${testidPrefix}-name`}
            aria-label="First name"
            className={inputCls}
          />
        )}
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          data-testid={`${testidPrefix}-email`}
          aria-label="Email address"
          className={inputCls}
        />
      </div>
      <IDButton
        as="button"
        type="submit"
        variant={dark ? "gold" : "solid"}
        disabled={busy}
        testid={`${testidPrefix}-submit`}
        className="mt-3 w-full justify-center !py-4 text-[14px]">
        {busy ? "Sending…" : cta}
      </IDButton>
      <p className={`id-sans mt-3.5 text-center text-[12px] ${dark ? "text-[var(--id-ivory)]/55" : "text-[var(--id-charcoal)]/45"}`}>
        {microcopy}
      </p>
    </form>
  );
}

export default SignupForm;
